import {
  Anchor,
  Avatar,
  Button,
  Center,
  Drawer,
  Group,
  Loader,
  Pagination,
  Pill,
  Rating,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { closeAllModals, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCircleCheck, IconHelp, IconMail, IconUser } from '@tabler/icons-react';
import cx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { PropsWithChildren, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  evaluateSession,
  exportPaginatedData,
  generateCSV,
  getReferencesService,
  getRobot,
  Markdown,
  PageData,
  queryMessageList,
  QueryMessageListParams,
  APIRobotInfo,
  SvgAlert,
  SvgThumbDownFilled,
  SvgThumbUpFilled,
  useBot,
  useGetFeedbackCheckbox,
  useNotIdFAQsMethods,
  useDateLocal,
  AgentMessage,
  isNewAgentType,
  PDFViewer,
  FileViewer,
  Page,
  cleanAgentContent,
} from '@mygpt/app';
import { MessageDetailInfo, ratingType, ReferenceSign } from '@mygpt/types';
import ChatbotImg from '/chatbot.png';
import { SvgEditNew } from '@/components/Common/icon';
import { FaqsItemEdit, FaqsItemEditProps } from '@/components/Faqs/item-edit';
import { RatingData, RatingItem } from '@/components/TeachModel/FeedbackTable';
import { I_UserFormValues } from '@/components/Widget/Embed/form-context';
import { useGPTStore } from '@/store';
import { MessageListInfo } from '@/types/interface';
import { removeSearchParam } from '@/utils/tool';
import { getRatingReasonFromMessage } from '@/utils/feedback';
import { confirmPartialExport } from '@/utils/exportConfirm';
import { formatPageHost } from '@/utils/pageUrl';

import classes from './index.module.css';
import sticky from '@/styles/stickyTable.module.css';

dayjs.extend(utc);

export const Conversation = ({
  dateRange,
  onDateRangeChange,
  setRangeDateTimeText,
}: {
  dateRange: DatesRangeValue;
  onDateRangeChange: (date: DatesRangeValue) => void;
  setRangeDateTimeText: (text: string) => void;
}) => {
  const { t } = useTranslation();

  const isLocalMode = import.meta.env.VITE_MODE === 'local';

  const { botId } = useBot();
  const { addFAQs, updateFAQs } = useNotIdFAQsMethods();
  const { dateLocale } = useDateLocal();
  const { robotInfo: storeRobotInfo } = useGPTStore();
  const feedbackCheckboxs = useGetFeedbackCheckbox();

  const [messageListLoading, setMessageListLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [messageList, setMessageList] = useState<Array<MessageListInfo>>([]);
  const [messageDetail, setMessageDetail] = useState<Array<MessageDetailInfo>>([]);
  const [selectedSession, setSelectedSession] = useState<MessageListInfo | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [ratingValue, setRatingValue] = useState<number>(0);
  const [ratingHoverValue, setRatingHoverValue] = useState<number>(0);
  const [scored, setScored] = useState<boolean>(false);
  const [searchBySessionId, setSearchBySessionId] = useState(false);
  const [userIdentifier, setUserIdentifier] = useState<string>('');
  const [userIdentifierList, setUserIdentifierList] = useState<{ value: string; label: string }[]>([]);
  const [urlKeyword, setUrlKeyword] = useState<string>('');
  // 输入 URL 关键词时防抖，避免每敲一个字符就请求一次
  const [debouncedUrlKeyword] = useDebouncedValue(urlKeyword.trim(), 700);

  const [robotInfo, setRobotInfo] = useState<APIRobotInfo>();
  const [page, setPage] = useState<{ page: number; total: number; size: number; pages: number }>({
    page: 1,
    total: messageList.length,
    pages: 1,
    size: 50,
  });

  const [typeValue, setTypeValue] = useState<string | null>('all');
  const [scoreValue, setScoreValue] = useState<string | null>('all');
  const TypeData = [
    { value: 'all', label: t('chat_history.source_type.all') },
    { value: 'playground', label: t('chat_history.source_type.playground') },
    { value: 'share', label: t('chat_history.source_type.share') },
    { value: 'widget', label: t('chat_history.source_type.widget') },
    { value: 'api', label: t('chat_history.source_type.api') },
    { value: 'realtime', label: t('chat_history.source_type.realtime') },
  ];

  const ScoreData = [
    { value: 'all', label: t('chat_history.score_type.all') },
    { value: 'scored', label: t('chat_history.score_type.scored') },
    { value: 'unscore', label: t('chat_history.score_type.unscore') },
  ];

  const ScoreDesc = [
    { title: t('chat_history.score.1_title'), desc: t('chat_history.score.1_desc') },
    { title: t('chat_history.score.2_title'), desc: t('chat_history.score.2_desc') },
    { title: t('chat_history.score.3_title'), desc: t('chat_history.score.3_desc') },
    { title: t('chat_history.score.4_title'), desc: t('chat_history.score.4_desc') },
    { title: t('chat_history.score.5_title'), desc: t('chat_history.score.5_desc') },
  ];

  const scoreDesc = useMemo(() => {
    if (ratingHoverValue > 0) {
      return ScoreDesc[ratingHoverValue - 1];
    } else if (ratingValue > 0) {
      return ScoreDesc[ratingValue - 1];
    } else {
      return { title: '', desc: '' };
    }
  }, [ratingValue, ratingHoverValue]);

  const sessionId = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('sessionId');
  }, [window.location.search]);

  const getParams = (customParam?: Partial<QueryMessageListParams>) => {
    return Object.assign(
      {
        aiId: botId,
        page: page.page,
        size: page.size,
      },
      dateRange[0] &&
        dateRange[1] && {
          startTime: dayjs(dateRange[0]).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
          endTime: dayjs(dateRange[1]).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
        },
      scoreValue === 'scored' && {
        min_score: 1,
      },
      scoreValue === 'unscore' && {
        max_score: 0,
      },
      typeValue !== 'all' && {
        source: typeValue,
      },
      userIdentifier && {
        user_identifier: userIdentifier,
      },
      debouncedUrlKeyword && {
        url_keyword: debouncedUrlKeyword,
      },
      customParam
    );
  };

  const getRobotInfo = async () => {
    let data = robotInfo;
    if (!data && botId) {
      const res = await getRobot({ aiId: botId });
      data = res.data;
    }
    if (!data) return;
    setRobotInfo(data);
  };

  useLayoutEffect(() => {
    getRobotInfo();
    getUserIdentifierList();
  }, []);

  const answerSource = useMemo(() => {
    if (!robotInfo?.configs?.widget_configs) return true;
    const widgetConfigs: I_UserFormValues = JSON.parse(robotInfo.configs.widget_configs);
    if (!widgetConfigs.chatbot?.answerSource) return true;
    return widgetConfigs.chatbot?.answerSource?.checked;
  }, [robotInfo?.configs?.widget_configs]);

  const getMessages = async () => {
    if (botId) {
      setMessageListLoading(true);
      const res = await queryMessageList(getParams({ session_id: sessionId || '' }));
      setMessageListLoading(false);
      const data: PageData<MessageListInfo> = res.data;
      if (!data) return;
      setPage({
        page: data.page,
        total: data.total,
        pages: data.pages,
        size: data.size,
      });
      setMessageList(data.items);

      if (sessionId) {
        handleSelectSession(data.items[0], 0);
        removeSearchParam('sessionId');
        setSearchBySessionId(true);
      } else {
        setSearchBySessionId(false);
      }
    }
  };

  const getUserIdentifierList = async () => {
    if (!botId) return;
    const res = await queryMessageList(getParams({ session_id: sessionId || '', page: 1, size: 1000 }));
    const data: PageData<MessageListInfo> = res.data;
    const list = new Set(
      data.items.filter((item) => Boolean(item.user_identifier)).map((item) => item.user_identifier)
    );
    setUserIdentifierList(Array.from(list).map((item) => ({ value: item, label: item })));
  };

  const updateMessageList = ({
    item,
    updateInfo,
  }: {
    item: MessageDetailInfo;
    updateInfo: { is_correct: boolean; faq_id?: string };
  }) => {
    if (selectedSession) {
      const updatedSession = {
        ...selectedSession,
        session_evaluate: {
          ...selectedSession.session_evaluate,
          correct_count: item.is_correct
            ? selectedSession.session_evaluate.correct_count
            : selectedSession.session_evaluate.correct_count + 1,
        },
        sessionmessages: selectedSession.sessionmessages.map((message) =>
          message.id === item.id ? { ...message, ...updateInfo } : message
        ),
      };

      const newMessageList = [...messageList];
      newMessageList[selectedIndex] = updatedSession;
      setSelectedSession(updatedSession);
      setMessageList(newMessageList);
    }
  };

  const editAnswerToFAQ = (item: MessageDetailInfo) => {
    const isEdit = !!item.faq_id;
    const onSubmit = async (params: Parameters<NonNullable<FaqsItemEditProps['onSubmit']>>[0]) => {
      const datasetId = params.datasetId || item.default_dataset_id;
      const commonParams = { datasetId, ...params, session_message_id: item.id };

      const updateInfo = { is_correct: true };

      const successTip = () => {
        showNotification({
          message: t('save_success'),
          color: 'green.5',
        });
      };

      try {
        if (isEdit) {
          await updateFAQs({ faqsId: item.faq_id || '', ...commonParams });
          setMessageDetail((state) =>
            state.map((message) => (message.id === item.id ? { ...message, ...updateInfo } : message))
          );
          updateMessageList({ item, updateInfo });
        } else {
          const res = await addFAQs(commonParams);
          setMessageDetail((state) =>
            state.map((message) => (message.id === item.id ? { ...message, faq_id: res.id, ...updateInfo } : message))
          );
          updateMessageList({ item, updateInfo: { faq_id: res.id, ...updateInfo } });
        }

        successTip();
      } catch (error) {
        console.error('Error:', error);
      } finally {
        closeAllModals();
      }
    };

    // 清理答案中的 agent 特殊标签（如 [TOOL_CALL]、[TOOL_RESPONSE] 等）
    const cleanedAnswer = cleanAgentContent(item.answer || '');

    const { citation, created_at, id, question, rating, user_id } = item;
    const faqs = {
      id,
      created_at,
      user_id,
      question,
      answer: cleanedAnswer,
      rating,
      sources: '',
      updated_at: '',
      has_image: false,
      answer_characters_count: 0,
      answer_token_count: 0,
    };
    openModal({
      title: isEdit ? t('faqs.edit_title') : t('faqs.add_title'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: 'lg',
      children: (
        <FaqsItemEdit
          selectDatasetId
          datasetId={item.default_dataset_id}
          faqs={faqs}
          citation={citation}
          onSubmit={onSubmit}
          onSubmitText={isEdit ? t('save') : t('faqs.action.add')}
        />
      ),
    });
  };

  useEffect(() => {
    if (selectedSession?.session_evaluate.score) {
      setRatingValue(selectedSession?.session_evaluate.score);
    }
  }, [selectedSession]);

  useEffect(() => {
    // 判断storeRobotInfo是为了解决：管理者通过会话链接访问时，此组件内部已经查询了详情并把参数清空了之后入口组件才处理跳转，导致跳转到协作页面没有弹出会话详情，确保先判断是否要跳转，再请求会话记录
    if (!botId || !storeRobotInfo) return;
    if (dateRange[0] && dateRange[1]) {
      getMessages();
    }
  }, [page.page, botId, storeRobotInfo]);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      if (page.page === 1) {
        getMessages();
      } else {
        setPage({ page: 1, pages: page.pages, total: page.total, size: page.size });
      }
    }
  }, [scoreValue, typeValue, dateRange, userIdentifier, debouncedUrlKeyword]);

  const getReference = (ai_id: string, message_id: string) => {
    return new Promise<ReferenceSign[]>((resolve) => {
      getReferencesService(ai_id, message_id).then((items) => {
        resolve(items.data);
      });
    });
  };

  const getSourceLabel = (source: string) => {
    return TypeData.find((_) => _.value === source)?.label;
  };

  // 会话本身不存 URL，取会话内第一条带 URL 的消息作为来源页面
  const getSessionPageUrl = (item: MessageListInfo) => {
    return item.sessionmessages?.find((msg) => msg.url)?.url || '';
  };

  const handleDrawerClose = () => {
    setSelectedSession(null);
    setMessageDetail([]);
    setRatingValue(0);
    // 指定sessionId查询后，关闭详情时查询全部数据
    if (searchBySessionId) {
      getMessages();
    }
  };

  const handleEvaluate = async () => {
    if (!selectedSession || ratingValue === 0) return;

    setSubmitLoading(true);
    await evaluateSession({
      aiId: selectedSession.robot_id,
      sessionId: selectedSession.session_id,
      score: ratingValue,
    });
    const newSelectedSession = {
      ...selectedSession,
      session_evaluate: {
        ...selectedSession.session_evaluate,
        score: ratingValue,
      },
    };
    const newMessageList = [...messageList];
    newMessageList[selectedIndex] = newSelectedSession;
    setSelectedSession(newSelectedSession);
    setMessageList(newMessageList);
    setSubmitLoading(false);
    setScored(true);
  };

  const ratingFilter = (rating: ratingType) => {
    switch (rating) {
      case 2:
        return t('teach_model.feedback_type.well');
      case -1:
        return t('teach_model.feedback_type.poorly');
      default:
        return '-';
    }
  };

  const ratingReasonFilter = (messageDetail: MessageDetailInfo) => {
    return getRatingReasonFromMessage(messageDetail, feedbackCheckboxs);
  };

  const handleExport = async () => {
    setExportLoading(true);

    try {
      const result = await exportPaginatedData<MessageListInfo>({
        fetchPage: async (page, size) => {
          const res = await queryMessageList(getParams({ page, size }));
          return res?.data as Page<MessageListInfo> | undefined;
        },
        onConfirmPartial: (fetched, total) => confirmPartialExport(t, fetched, total),
      });

      if (!result) return;

      if (result.partial) {
        showNotification({
          message: t('chat_history.export.partial_success', {
            fetched: result.items.length,
            total: result.total,
          }),
          color: 'yellow.5',
        });
      }

      exportCSV(result.items);
    } catch (error) {
      console.error('批量获取数据失败:', error);
      showNotification({
        message: t('export_failed'),
        color: 'red.5',
      });
    } finally {
      setExportLoading(false);
    }
  };

  const exportCSV = (items: MessageListInfo[]) => {
    const time =
      dateRange[0] && dateRange[1]
        ? ` ${dayjs(dateRange[0]).format('YYYY.MM.DD')}_${dayjs(dateRange[1]).format('YYYY.MM.DD')}`
        : '';

    const fileName = `${t('chat_history.tab.history_title')}_${time}`;
    // 创建CSV的头部行
    const header = [
      isLocalMode && t('chat_history.message_detail.session_id'),
      t('chat_history.table.last_conv_time'),
      t('chat_history.table.title'),
      t('chat_history.table.created_at'),
      t('chat_history.question'),
      t('chat_history.answer'),
      t('teach_model.user_feedback'),
      t('teach_model.table.rating'),
      t('teach_model.table.feedback_details'),
      t('chat_history.table.type'),
      t('chat_history.table.message'),
      t('chat_history.table.unanswerable'),
      t('chat_history.table.correction'),
      t('chat_history.table.score'),
      t('chat_history.message_detail.session_id'),
      t('chat_history.table.source_url'),
      t('chat_history.table.user'),
      t('settings.feedback.has_feedback'),
    ].filter(Boolean);

    // 将每个数据对象转换为CSV格式的一行
    const rows = items.flatMap((item) => {
      const messages = item.sessionmessages;
      const evaluate = item.session_evaluate;
      // 防御：会话可能没有任何消息，此时仍保留会话级信息，仅对消息级字段做兜底
      const firstMsg = messages?.[0];
      const mainRow = [
        isLocalMode ? item.session_id : null,
        dayjs(item.updated_at).format('YYYY-MM-DD HH:mm:ss'),
        item.title,
        firstMsg ? dayjs(firstMsg.created_at).format('YYYY-MM-DD HH:mm:ss') : '-',
        firstMsg?.question ?? '-',
        cleanAgentContent(firstMsg?.answer),
        firstMsg ? ratingFilter(firstMsg.rating) : '-',
        firstMsg ? ratingReasonFilter(firstMsg) : '-',
        firstMsg?.feedback_content || '-',
        getSourceLabel(item.source) || item.source,
        evaluate?.message_count?.toString() ?? '-',
        evaluate?.unanswerable_count?.toString() ?? '-',
        evaluate?.correct_count?.toString() ?? '-',
        evaluate?.score?.toString() ?? '-',
        item.session_id,
        firstMsg?.url || '-',
        item.user_identifier || '-',
        item.transfer_to_human ? '✓' : '',
      ].filter((item) => item !== null);

      // 消息的行数据：第 2+ 条消息属于同一 session，session 级字段与首行共用，留空即可
      const additionalRows = (messages ?? [])
        .slice(1)
        .map((msg) =>
          [
            isLocalMode ? '' : null,
            '',
            '',
            dayjs(msg.created_at).format('YYYY-MM-DD HH:mm:ss'),
            msg.question,
            cleanAgentContent(msg.answer),
            ratingFilter(msg.rating),
            ratingReasonFilter(msg),
            msg.feedback_content || '-',
            '',
            '',
            '',
            '',
            '',
            '',
            msg.url || '-',
            '',
            msg.transfer_to_human ? '✓' : '',
          ].filter((item) => item !== null)
        );

      return [mainRow, ...additionalRows];
    });
    const csvData = {
      header,
      rows,
    };

    generateCSV(fileName, csvData);
  };

  const getRatingType = (rating: ratingType) => {
    if (rating === 2) return 'good';
    if (rating === -1) return 'bad';
  };

  const getFeedbackDetail = (item: MessageDetailInfo) => {
    const rating: RatingData[] = [];
    const type = getRatingType(item.rating);
    type &&
      item.session_message_metadata?.map((meta) => {
        if (meta.key !== 'value') return;
        if (!meta.value) return;
        let value: unknown;
        try {
          value = JSON.parse(meta.value.replace(/'/g, '"'));
        } catch (e) {
          console.warn('Failed to parse session_message_metadata, skipped:', meta.value, e);
          return;
        }
        if (!Array.isArray(value)) return;
        value.map((metaValue: string) => {
          rating.push({
            type,
            text: metaValue,
          });
        });
      });
    return (
      <Stack gap={4}>
        {rating.map((item, index) => {
          return <RatingItem key={index} data={item} />;
        })}
        <Text size="sm">{item.feedback_content}</Text>
      </Stack>
    );
  };

  const handleSelectSession = (item: MessageListInfo, index: number) => {
    if (!item) return;
    setSelectedSession(item);
    setMessageDetail([...item.sessionmessages]);
    setSelectedIndex(index);
    setScored(false);
  };

  const CustomTooltip = ({
    children,
    disabled,
    label,
  }: PropsWithChildren<{ label: JSX.Element; disabled: boolean }>) => {
    return (
      <Tooltip
        label={
          <Stack gap={12}>
            <Text>{t('chat_history.message_detail.feedback')}</Text>
            {label}
          </Stack>
        }
        position="bottom-end"
        arrowOffset={16}
        disabled={disabled}
        withArrow
        multiline
        className="max-w-[500px]"
      >
        <div>{children}</div>
      </Tooltip>
    );
  };

  const ColorPill = ({ children, color = 'bg-violet-600' }: PropsWithChildren<{ color?: string }>) => {
    return <Pill className={`${color} text-white`}>{children}</Pill>;
  };

  return (
    <div className="h-full">
      <div className="h-full">
        <Group justify="space-between" mb={20}>
          <Title order={4} fw={500}>
            {t('chat_history.tab.history_title')}
          </Title>
          <Group>
            <TextInput
              size="sm"
              style={{ width: 200 }}
              value={urlKeyword}
              onChange={(e) => setUrlKeyword(e.target.value)}
              placeholder={t('chat_history.tab.url_placeholder')}
            />
            <Select
              size="sm"
              style={{ width: 150 }}
              value={userIdentifier}
              onChange={(value: string | null) => {
                setUserIdentifier(value || '');
              }}
              data={[{ value: '', label: t('chat_history.user_identifier.all') }, ...userIdentifierList]}
              searchable
            />
            <Select
              size="sm"
              style={{ width: 150 }}
              value={typeValue}
              onChange={(value: string | null) => {
                setTypeValue(value);
              }}
              data={TypeData}
              allowDeselect={false}
            />
            <Select
              size="sm"
              style={{ width: 150 }}
              value={scoreValue}
              onChange={(value: string | null) => {
                setScoreValue(value);
              }}
              data={ScoreData}
              allowDeselect={false}
            />

            <DatePickerInput
              popoverProps={{
                middlewares: {
                  flip: false,
                },
                position: 'top',
              }}
              type="range"
              placeholder={t('dashboard.date.select')}
              locale={dateLocale}
              size="sm"
              value={dateRange}
              onChange={(value) => {
                onDateRangeChange(value);
                setRangeDateTimeText(t('dashboard.date.custom_date'));
              }}
              valueFormat="YYYY-MM-DD"
              numberOfColumns={2}
              allowSingleDateInRange
            />

            <Button
              onClick={handleExport}
              loading={exportLoading}
              disabled={!dateRange[0] || !dateRange[1] || messageList.length === 0}
            >
              {t('teach_model.export_log')}
            </Button>
          </Group>
        </Group>
        {messageListLoading ? (
          <div className="h-[300px] bg-[#F8F6FF] flex items-center justify-center">
            <Loader />
          </div>
        ) : messageList.length === 0 ? (
          <div className="h-[300px] bg-[#F8F6FF] flex items-center justify-center">
            <span className="text-dark-600 ml-2">{t('no_data')}</span>
          </div>
        ) : (
          // 列数多、日语表头长，容器内横向滚动，避免把页面撑出去；圆角放到滚动容器上
          <Table.ScrollContainer minWidth={1000} className="rounded-lg">
            {/* 主题给 table 设了 overflow: hidden，会让 sticky 单元格相对 table 而不是滚动容器定位，这里放开 */}
            <Table styles={{ table: { overflow: 'visible' } }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th className={cx(sticky.stickyCol, classes.stickyColTime)}>
                    {t('chat_history.table.last_conv_time')}
                  </Table.Th>
                  <Table.Th className={cx(sticky.stickyCol, classes.stickyColTitle)}>
                    {t('chat_history.table.title')}
                  </Table.Th>
                  <Table.Th>{t('chat_history.table.type')}</Table.Th>
                  <Table.Th>
                    <Center>{t('chat_history.table.message')}</Center>
                  </Table.Th>
                  <Table.Th>
                    <Center className="gap-1">
                      {t('chat_history.table.unanswerable')}
                      <Tooltip
                        label={t('chat_history.table.unanswered_tip')}
                        multiline
                        withArrow
                        withinPortal
                        className="max-w-[300px]"
                      >
                        <span>
                          <IconHelp className="cursor-pointer" size={16} />
                        </span>
                      </Tooltip>
                    </Center>
                  </Table.Th>
                  <Table.Th>
                    <Center>{t('chat_history.table.correction')}</Center>
                  </Table.Th>
                  <Table.Th>{t('chat_history.table.source_url')}</Table.Th>
                  <Table.Th>{t('chat_history.table.user')}</Table.Th>
                  <Table.Th>
                    <Center>{t('chat_history.table.score')}</Center>
                  </Table.Th>
                  <Table.Th className={cx(sticky.stickyCol, classes.stickyColAction)}>
                    {t('chat_history.table.action')}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {messageList.map((item, index) => (
                  <Table.Tr key={item.session_id}>
                    <Table.Td className={cx(sticky.stickyCol, classes.stickyColTime)}>
                      <Text c="gray.4" size="sm">
                        {dayjs(item.updated_at).format('YYYY-MM-DD HH:mm:ss')}
                      </Text>
                    </Table.Td>
                    <Table.Td className={cx(sticky.stickyCol, classes.stickyColTitle)}>
                      <Tooltip label={item.title} withinPortal multiline w={300} className="break-all" withArrow>
                        <Group
                          gap={4}
                          wrap="nowrap"
                          className={`${item.transfer_to_human && 'ml-[-18px]'} whitespace-nowrap`}
                        >
                          {item.transfer_to_human && <IconMail size={14} className="text-violet-500" />}
                          {item.title.length >= 20 && `${item.title.substring(0, 20)}...`}
                          {item.title.length < 20 && item.title}
                        </Group>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td className="whitespace-nowrap">{getSourceLabel(item.source) || item.source}</Table.Td>
                    <Table.Td>
                      <Text ta="center">{item.session_evaluate.message_count}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group justify="center">
                        {item.session_evaluate.unanswerable_count > 0 ? (
                          <ColorPill color="bg-red-600">{item.session_evaluate.unanswerable_count}</ColorPill>
                        ) : (
                          <Pill>{item.session_evaluate.unanswerable_count}</Pill>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group justify="center">
                        {item.session_evaluate.correct_count > 0 ? (
                          <ColorPill>{item.session_evaluate.correct_count}</ColorPill>
                        ) : (
                          <Pill>0</Pill>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ maxWidth: 160 }}>
                      {getSessionPageUrl(item) ? (
                        <Tooltip label={getSessionPageUrl(item)} withinPortal multiline w={300} className="break-all">
                          <Anchor
                            href={getSessionPageUrl(item)}
                            target="_blank"
                            rel="noreferrer"
                            size="sm"
                            truncate="end"
                          >
                            {formatPageHost(getSessionPageUrl(item))}
                          </Anchor>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </Table.Td>
                    <Table.Td style={{ maxWidth: 180 }}>
                      <Tooltip label={item.user_identifier} disabled={!item.user_identifier} withinPortal>
                        <Text size="sm" truncate="end">
                          {item.user_identifier}
                        </Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Stack align="center" gap={2}>
                        <Rating value={item.session_evaluate.score} fractions={10} size="xs" readOnly />
                        <Text size="xs" c="gray">
                          {item.session_evaluate.score > 0 ? ScoreDesc[item.session_evaluate.score - 1].title : '-'}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td className={cx(sticky.stickyCol, classes.stickyColAction)}>
                      <Button
                        size="xs"
                        onClick={() => {
                          handleSelectSession(item, index);
                        }}
                      >
                        {item.session_evaluate.score > 0 ? t('edit') : t('chat_history.table.score_btn')}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}

        {!!page.total && (
          <Group justify="space-between" mt={15}>
            <Text size={'sm'} c="gray.5">
              {t('teach_model.table.total_items', { number: page.total })}
            </Text>
            <Pagination
              value={page.page}
              size={'sm'}
              onChange={(value) => {
                setPage((state) => ({ ...state, page: value }));
              }}
              total={page.pages}
              color="violet.5"
            />
          </Group>
        )}
      </div>

      <Drawer
        opened={messageDetail.length > 0}
        onClose={handleDrawerClose}
        title={
          <Stack gap={2}>
            <Text fw={500}>{t('chat_history.message_detail_title')}</Text>
            <Text c="gray.6" size="sm">
              {t('chat_history.message_detail_desc')}
            </Text>
            {isLocalMode && (
              <Text c="gray.4" size="xs">
                {t('chat_history.message_detail.session_id')}
                {': '}
                {selectedSession?.session_id}
              </Text>
            )}
          </Stack>
        }
        position="right"
        className={`relative ${classes.messageDetail}`}
        size="80%"
        scrollAreaComponent={ScrollArea}
      >
        <div className="h-full pb-[50px]">
          {messageDetail?.map((item, index) => {
            return (
              <li key={Math.random()} className="pr-4 group rounded-md list-none">
                <div className="relative p-2 pb-5">
                  {item.question && (
                    <div className="flex mb-2 gap-2">
                      <Avatar radius="xl" variant="filled" color="violet.5">
                        <IconUser size={20} />
                      </Avatar>
                      <div className="grow">
                        <div
                          className="inline-block bg-white p-4 rounded-lg rounded-ss-none max-w-[1000px] text-left break-words shadow"
                          style={{ wordBreak: 'break-word' }}
                        >
                          <Text size="xs" c="gray.5" className="mb-2">
                            {selectedSession?.user_identifier || t('chat_history.message_detail.customer')}
                            {' · '}
                            {dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss')}
                          </Text>
                          {item.question}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex mb-2 gap-2">
                    <Avatar radius="xl" color="indigo" variant="filled" src={ChatbotImg}></Avatar>
                    <div className="group">
                      <div
                        className={cx(
                          'relative inline-block bg-white p-4 rounded-lg rounded-ss-none mt-2 max-w-[1000px] shadow',
                          classes.shadow
                        )}
                      >
                        <Group justify="space-between" className="mb-2">
                          <Group className="py-1">
                            <Text size="xs" c="gray.5">
                              {item.comes_from === 'faq'
                                ? t('chat_history.message_detail.qa_response')
                                : t('chat_history.message_detail.ai_response')}
                              {' · '}
                              {dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss')}
                            </Text>
                            {item.is_correct && (
                              <Group className="text-violet-400" gap={4}>
                                <IconCircleCheck size={12} />
                                <Text size="xs" c="violet.4">
                                  {t('chat_history.message_detail.edited')}
                                </Text>
                              </Group>
                            )}
                            {item.transfer_to_human && (
                              <Group className="text-violet-400" gap={4}>
                                <IconMail size={14} />
                                <Text size="xs" c="violet.4">
                                  {t('settings.feedback.has_feedback')}
                                </Text>
                              </Group>
                            )}
                          </Group>

                          <Group>
                            {item.answer && (
                              <div className="hidden group-hover:block">
                                <Button
                                  color="violet.5"
                                  onClick={() => editAnswerToFAQ(item)}
                                  size="compact-xs"
                                  radius="xl"
                                >
                                  <SvgEditNew />
                                  <span className="ml-1">
                                    {item.is_correct ? t('edit') : t('chat_history.correction_btn')}
                                  </span>
                                </Button>
                              </div>
                            )}
                            {!!item.rating && (
                              <div>
                                {item.rating === 2 && (
                                  <CustomTooltip
                                    label={getFeedbackDetail(item)}
                                    disabled={
                                      !!item.session_message_metadata?.length &&
                                      item.session_message_metadata[1].value === '[]' &&
                                      !item.feedback_content
                                    }
                                  >
                                    <SvgThumbUpFilled />
                                  </CustomTooltip>
                                )}
                                {item.rating === -1 && (
                                  <CustomTooltip
                                    label={getFeedbackDetail(item)}
                                    disabled={
                                      !!item.session_message_metadata?.length &&
                                      item.session_message_metadata[1].value === '[]' &&
                                      !item.feedback_content
                                    }
                                  >
                                    <SvgThumbDownFilled />
                                  </CustomTooltip>
                                )}
                              </div>
                            )}
                          </Group>
                        </Group>
                        {item.answer ? (
                          // llm_can_answer === false 时统一标红（含 agent 类型），其余按类型分别渲染
                          item.llm_can_answer === false ? (
                            <Text c="red.5">{cleanAgentContent(item.answer)}</Text>
                          ) : isNewAgentType(robotInfo?.robot_type) ? (
                            <AgentMessage content={item.answer} />
                          ) : (
                            <Markdown
                              content={item.answer}
                              footnotes={answerSource ? item.footnotes : []}
                              referenceList={answerSource ? item.reference_list : []}
                              referenceIndexes={answerSource ? item.reference_indexes : []}
                              getReference={answerSource ? () => getReference(botId, item.id) : undefined}
                            />
                          )
                        ) : (
                          <div className="text-[14px] leading-7">
                            <div className="flex items-center gap-2 text-red-500">
                              <SvgAlert />
                              {t('completions.error')}
                            </div>
                            <div className="flex gap-2 flex-col sm:flex-row items-start sm:items-center">
                              <Text c={'red.4'}>{t('errors.answer.description')}</Text>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </div>

        <div className="absolute bottom-0 left-0 w-full px-10">
          {scored ? (
            <Group className="bg-yellow-100 py-5 px-6 rounded-t-xl text-yellow-800">
              <Text>{t('chat_history.scored_desc')}</Text>
              <IconCircleCheck />
            </Group>
          ) : (
            <Group className="bg-yellow-100 py-4 px-6 rounded-t-xl text-yellow-800" justify="space-between">
              <Text>{t('chat_history.score_desc')}</Text>
              <Group wrap="nowrap">
                <Text>{scoreDesc.title}</Text>
                <Tooltip label={scoreDesc.desc} withArrow>
                  <Rating
                    size="lg"
                    className="gap-2"
                    value={ratingValue}
                    onChange={setRatingValue}
                    onHover={setRatingHoverValue}
                  />
                </Tooltip>

                <Button size="sm" radius="xl" color="yellow" onClick={handleEvaluate} loading={submitLoading}>
                  {t('submit')}
                </Button>
              </Group>
            </Group>
          )}
        </div>
      </Drawer>
      <PDFViewer />
      <FileViewer />
    </div>
  );
};
